import React, { useState } from 'react';
import axios from 'axios';
import API_URL from '../../config';
import { showError } from '../../utils/toast';
import {
  FLEET_BASE_MONTHLY_DOLLARS,
  FLEET_PER_VEHICLE_MONTHLY_DOLLARS,
  ONBOARDING_CHOOSE_PLAN,
  ONBOARDING_PLAN_CARDS,
  ONBOARDING_PLAN_LABELS,
  fleetMonthlyTotalDollars,
} from '../../config/pricingPublic';
import PromoCodeInput from '../Subscriptions/PromoCodeInput';
import '../../App.css';

const FLEET_QTY_MIN = 1;
const FLEET_QTY_MAX = 25;

function normalizeSelectedPlan(plan) {
  if (plan === 'pro') return 'fleet';
  return plan || 'free';
}

const PlanStep = ({ onContinue, onBack, onboardingData, setOnboardingData }) => {
  const [selectedPlan, setSelectedPlan] = useState(
    normalizeSelectedPlan(onboardingData.selectedPlan)
  );
  const [loading, setLoading] = useState(false);
  const [truckQty, setTruckQty] = useState(FLEET_QTY_MIN);
  const [promo, setPromo] = useState(null);

  const handleContinue = async () => {
    setOnboardingData({ ...onboardingData, selectedPlan });
    onContinue({ selectedPlan });
  };

  const startCheckout = async (tier) => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const body = { tier };
      if (tier === 'fleet') {
        body.quantity = truckQty;
        body.returnTo = 'onboarding';
      }
      if (promo) {
        body.promoCode = promo.code;
      }
      const { data } = await axios.post(
        `${API_URL}/api/subscriptions/create-checkout`,
        body,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (data?.checkoutUrl) {
        window.location.href = data.checkoutUrl;
      }
    } catch (err) {
      showError(err.response?.data?.message || 'Failed to start checkout');
    } finally {
      setLoading(false);
    }
  };

  const continueLabel =
    ONBOARDING_PLAN_LABELS[selectedPlan] || selectedPlan;

  return (
    <div className="onboarding-content" id="sovrynn-onboarding-plan-step">
      <h2 className="step-title">{ONBOARDING_CHOOSE_PLAN.title}</h2>
      <p className="step-description">{ONBOARDING_CHOOSE_PLAN.description}</p>

      <PromoCodeInput
        applied={promo}
        onApplied={setPromo}
        onRemoved={() => setPromo(null)}
        disabled={loading}
      />

      <div className="plan-options">
        {ONBOARDING_PLAN_CARDS.map((card) => {
          const isSelected = selectedPlan === card.tier;
          const classNames = [
            'plan-option',
            card.featured ? 'featured' : '',
            isSelected ? 'selected' : '',
          ]
            .filter(Boolean)
            .join(' ');

          const onPrimary = () => {
            if (card.checkoutTier) {
              startCheckout(card.checkoutTier);
            } else {
              setSelectedPlan(card.tier);
            }
          };

          return (
            <div key={card.tier} className={classNames}>
              <h3>{card.title}</h3>
              <p className="plan-price">{card.priceLine}</p>
              <p className="plan-blurb">{card.blurb}</p>
              <ul>
                {card.features.map((line) => (
                  <li key={line}>{line}</li>
                ))}
              </ul>
              {card.checkoutTier === 'fleet' && (
                <div className="form-group" style={{ margin: '0 0 16px 0' }}>
                  <label htmlFor="onboarding-trucks-to-track">Trucks to track</label>
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 12,
                      marginBottom: 8,
                    }}
                  >
                    <button
                      type="button"
                      className="btn btn-secondary"
                      aria-label="Fewer trucks"
                      disabled={loading || truckQty <= FLEET_QTY_MIN}
                      onClick={() =>
                        setTruckQty((n) => Math.max(FLEET_QTY_MIN, n - 1))
                      }
                    >
                      −
                    </button>
                    <span
                      id="onboarding-trucks-to-track"
                      aria-live="polite"
                      style={{ minWidth: 28, textAlign: 'center', fontWeight: 700 }}
                    >
                      {truckQty}
                    </span>
                    <button
                      type="button"
                      className="btn btn-secondary"
                      aria-label="More trucks"
                      disabled={loading || truckQty >= FLEET_QTY_MAX}
                      onClick={() =>
                        setTruckQty((n) => Math.min(FLEET_QTY_MAX, n + 1))
                      }
                    >
                      +
                    </button>
                  </div>
                  <p className="helper-text" style={{ margin: 0 }}>
                    ${FLEET_BASE_MONTHLY_DOLLARS} base + {truckQty} trucks × $
                    {FLEET_PER_VEHICLE_MONTHLY_DOLLARS} = $
                    {fleetMonthlyTotalDollars(truckQty)}/mo
                  </p>
                </div>
              )}
              <button
                type="button"
                className={
                  card.checkoutTier ? 'btn btn-primary' : 'btn btn-secondary'
                }
                onClick={onPrimary}
                disabled={loading}
              >
                {loading ? 'Loading...' : card.ctaLabel}
              </button>
            </div>
          );
        })}
      </div>

      <div className="onboarding-buttons">
        <button type="button" className="btn btn-secondary" onClick={onBack}>
          Back
        </button>
        <button type="button" className="btn btn-primary" onClick={handleContinue}>
          Continue with {continueLabel} →
        </button>
      </div>
    </div>
  );
};

export default PlanStep;
